import config from '../../../config';

import * as apiConstants from '../apiConstants';
import * as dataUtils from '../../../utils/dataUtils';
import * as urlUtils from '../../../utils/urlUtils';
import * as uuidUtils from '../../../utils/uuidUtils';

import ApiRequest from './ApiRequest';

import authService from '../../AuthService';


const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 50;

const DEFAULT_ENDPOINT = config.endpoints.default;


/**
 * Builder API to fluently create API requests. Supports endpoint specific configuration.
 * Modified builder pattern to pass data directly to building object instead of local holding.
 *
 * @see src/config/config.default.js : config.endpoints
 */
class ApiRequestBuilder {

    /**
     * @type {object} Endpoint configuration storing object
     */
    endpointConfig;


    /**
     * @type {object|null} Pagination data
     */
    pageData = null;


    /**
     * @type {ApiRequest}
     */
    request;


    /**
     * @param method {string}
     * @param path {string}
     * @param endpoint {string|null} Endpoint identifier as in configuration, defaults to 'backend'
     * @private
     */
    constructor(method, path, endpoint = null) {

        this.request = new ApiRequest(path, method);

        return this.endpoint(endpoint)
            .method(method)
            .path(path);
    }


    /**
     * @param body {string|object|any} Any payload that can be serialized
     * @returns {ApiRequestBuilder}
     */
    body(body) {

        this.request.setBody(body);

        return this;
    }


    /**
     * @param method {string} HTTP method
     * @returns {ApiRequestBuilder}
     */
    method(method) {

        this.request.setMethod(method);

        return this;
    }


    /**
     * @param path {string} Request path with path variable placeholders. Supported place holders are:
     *                        1. /path/:varName/anotherPathSegment
     *                        2. /path/{varName}/anotherPathSegment
     * @returns {ApiRequestBuilder}
     */
    path(path) {

        this.request.setPath(path);

        return this;
    }


    /**
     * @param name {string}
     * @param value {string|any}
     * @returns {ApiRequestBuilder}
     */
    pathParam(name, value) {

        this.request.setPathVariable(name, value);

        return this;
    }


    /**
     * @param pathParams {object} Key value object providing multiple path params
     * @returns {ApiRequestBuilder}
     */
    pathParams(pathParams) {

        Object.entries(pathParams)
            .forEach(([name, value]) => this.pathParam(name, value));

        return this;
    }


    /**
     * @param name {string}
     * @param value {string|any}
     * @returns {ApiRequestBuilder}
     */
    queryParam(name, value) {

        this.request.setQueryParameter(name, value);

        return this;
    }


    /**
     * @param queryParams {object} Key value object providing multiple query params
     * @returns {ApiRequestBuilder}
     */
    queryParams(queryParams) {

        Object.entries(queryParams)
            .forEach(([name, value]) => this.queryParam(name, value));

        return this;
    }


    /**
     * @param dispatchType {string|number}
     * @param callback {function}
     * @return {ApiRequestBuilder}
     */
    dispatch(dispatchType, callback) {

        this.request.addDispatch(dispatchType, callback);

        return this;
    }


    /**
     * @param callback {function} Function called when the request is being started
     * @returns {ApiRequestBuilder} Builder instance
     */
    onInit(callback) {

        return this.dispatch(apiConstants.REQUEST_INIT, callback);
    }


    /**
     * @param callback {function} Function called if anything technical goes wrong with the request e.g. not reachable
     * @returns {ApiRequestBuilder} Builder instance
     */
    onFailure(callback) {

        return this.dispatch(apiConstants.REQUEST_FAILURE, callback);
    }


    /**
     * @param callback {function} Function called if the status code is any 2xx
     * @returns {ApiRequestBuilder} Builder instance
     */
    onSuccess(callback) {

        return this.dispatch(apiConstants.REQUEST_SUCCESS, callback);
    }


    /**
     * @param httpStatus {number} Http status code to be called on
     * @param callback {function} Function called if the status code is any 2xx
     * @returns {ApiRequestBuilder} Builder instance
     */
    onStatus(httpStatus, callback) {

        return this.dispatch(httpStatus.toString(10), callback);
    }


    /**
     * Convenience builder method
     *
     * @returns {ApiRequestBuilder} Content-Type application/x-www-form-urlencoded configured api request
     */
    form() {

        return this.header(apiConstants.HTTP_HEADER_CONTENT_TYPE, apiConstants.HTTP_HEADER_CONTENT_TYPE_FORM);
    }


    /**
     * Convenience builder method
     *
     * @returns {ApiRequestBuilder} Content-Type application/json configured api request
     */
    json() {

        return this.header(apiConstants.HTTP_HEADER_CONTENT_TYPE, apiConstants.HTTP_HEADER_CONTENT_TYPE_JSON);
    }


    /**
     * @param endpoint {string|null} Endpoint identifier
     * @returns {ApiRequestBuilder}
     */
    endpoint(endpoint) {

        const endpointConfig = config.endpoints[endpoint || DEFAULT_ENDPOINT];
        if (!endpointConfig) {

            throw new Error('Endpoint config for `' + endpoint + '` not configured');
        }

        this.endpointConfig = endpointConfig;

        return this;
    }


    /**
     * @returns {ApiRequest}
     */
    build() {

        const url = this.buildUrl(this.request);
        this.request.setUrl(url);

        // log-related
        const uuid = uuidUtils.generateUuid();
        this.request.setHeader('X-Request-Id', uuid);
        this.request.setHeader('X-Requested-With', 'XMLHttpRequest');

        // form needs special treatment
        if (!this.request.isForm()) {

            return this.request;
        }

        const body = this.request.getBody();
        const encodedBody = this.encodeNameValuePairs(body || {});
        this.request.setBody(encodedBody);

        return this.request;
    }


    /**
     * @param {ApiRequest} request
     * @return {string} Final request URL
     * @private
     */
    buildUrl(request) {

        const baseUrl = this.buildBaseUrl();
        const path = urlUtils.buildPath(request.getPath(), request.getPathVariables());
        const queryString = this.buildQueryString(request.getQueryParameters());
        const queryStringSeparator = queryString.length > 0 ? '?' : '';

        return `${baseUrl}${path}${queryStringSeparator}${queryString}`;
    }


    /**
     * @returns {string}
     * @private
     */
    buildBaseUrl() {

        const {
            host,
            path
        } = this.endpointConfig;

        return `${host}${path}`;
    }


    /**
     * @param path {string} Request path including possible path variable placeholders
     * @param pathVariables {object} Path variables
     * @returns {string} Parsed path
     */
    buildPath(path, pathVariables) {

        Object.keys(pathVariables).forEach(pathVariableName => {

            const pathVariableValue = pathVariables[pathVariableName];

            // supporting both styles of path variable declaration
            path = path.replace(`:${pathVariableName}`, pathVariableValue);
            path = path.replace(`{${pathVariableName}}`, pathVariableValue);
        });

        return path;
    }


    /**
     * @param data {object} Data to encode
     * @return {string} Name value pairs URI encoded
     */
    encodeNameValuePairs = data => {

        const pairs = Object.entries(data).map(([name, value]) => {

            return ApiRequestBuilder.createQueryParameter(name, value);
        });

        if (pairs.length === 0) {

            return '';
        }

        return pairs.join('&');
    };


    /**
     * @param parameters {object}
     * @returns {string}
     */
    buildQueryString = parameters => {

        const paginationQueryStringData = this.buildPaginationQueryString();
        const queryStringData = dataUtils.merge(parameters, paginationQueryStringData);

        return this.encodeNameValuePairs(queryStringData);
    };


    /**
     * Creates a single name=value pair for the query-string
     *
     * @param name {string} Name
     * @param value {string} Value
     * @param encodeValue {boolean} True encodes the value; false not
     * @returns {string} Key-value-pair URI encoded
     */
    static createQueryParameter(name, value, encodeValue = true) {

        const queryStringValue = encodeValue ? encodeURIComponent(value) : value;

        return encodeURIComponent(name) + '=' + queryStringValue;
    }


    /**
     * @returns {object} Pagination query string data
     */
    buildPaginationQueryString() {

        const pageData = this.pageData;
        if (!pageData) {

            return {};
        }

        const simplePaginationParameters = {
            page: pageData.page || DEFAULT_PAGE,
            size: pageData.size || DEFAULT_SIZE
        };

        const sortValues = [];
        let lastDirection = null;

        dataUtils.each(pageData.sort || {}, (sortDirection, property) => {

            sortValues.push(property);
            lastDirection = sortDirection;
        });
        if (sortValues.length > 0) {
            sortValues.push(lastDirection);
            simplePaginationParameters.sort = sortValues.join(',');
        }

        return simplePaginationParameters;
    }


    /**
     * @param name {string} Header name
     * @param value {string} Any header value
     * @return {ApiRequestBuilder}
     */
    header(name, value) {

        this.request.setHeader(name, value);

        return this;
    }


    /**
     * Creates a pagination request when built
     *
     * @param pageData {object} Page data
     * @returns {ApiRequestBuilder}
     */
    withPagination(pageData) {

        this.pageData = pageData;

        return this;
    }


    /**
     * Sets the bearer token in in the authorization header
     *
     * @return {ApiRequestBuilder}
     */
    withToken() {

        const authToken = authService.getToken();
        if (!authToken) {

            throw Error('No token available for withToken request build');
        }
        const headerValue = `${apiConstants.HTTP_HEADER_AUTHORIZATION_BEARER} ${authToken}`;

        return this.header(apiConstants.HTTP_HEADER_AUTHORIZATION, headerValue);
    }


    /**
     * @param path {string} Path supporting place holders
     * @param endpoint {string|null} Optional endpoint identifier
     * @returns {ApiRequestBuilder}
     */
    static delete(path, endpoint = null) {

        return new ApiRequestBuilder(apiConstants.HTTP_METHOD_DELETE, path, endpoint);
    }


    /**
     * @param path {string} Path supporting place holders
     * @param endpoint {string|null} Optional endpoint identifier
     * @returns {ApiRequestBuilder}
     */
    static get(path, endpoint = null) {

        return new ApiRequestBuilder(apiConstants.HTTP_METHOD_GET, path, endpoint);
    }


    /**
     * @param path {string} Path supporting place holders
     * @param endpoint {string|null} Optional endpoint identifier
     * @returns {ApiRequestBuilder}
     */
    static head(path, endpoint = null) {

        return new ApiRequestBuilder(apiConstants.HTTP_METHOD_HEAD, path, endpoint);
    }


    /**
     * @param path {string} Path supporting place holders
     * @param endpoint {string|null} Optional endpoint identifier
     * @returns {ApiRequestBuilder}
     */
    static patch(path, endpoint = null) {

        return new ApiRequestBuilder(apiConstants.HTTP_METHOD_PATCH, path, endpoint);
    }


    /**
     * @param path {string} Path supporting place holders
     * @param endpoint {string|null} Optional endpoint identifier
     * @returns {ApiRequestBuilder}
     */
    static put(path, endpoint = null) {

        return new ApiRequestBuilder(apiConstants.HTTP_METHOD_PUT, path, endpoint);
    }


    /**
     * @param path {string} Path supporting place holders
     * @param endpoint {string|null} Optional endpoint identifier
     * @returns {ApiRequestBuilder}
     */
    static post(path, endpoint = null) {

        return new ApiRequestBuilder(apiConstants.HTTP_METHOD_POST, path, endpoint);
    }
}


export default ApiRequestBuilder;
